/**
 * The store-backed fact sources (CAPABILITIES.md §3.2). Each memoises on its inputs, so facts keep
 * their identity until something they depend on changes. Takes the store, so a test can pass one.
 */
import { KIOSK_ENTRY_ENABLED } from "../../kiosk/featureFlag";
import { selectKioskEnabled } from "../../kiosk/config/selectors";
import type { FactSources, Source } from "../engine";
import type { EnvFacts } from "../types";
import { configFrom, subjectFrom, type UserRecord } from "./user";
import { detectPlatform } from "./platform";

export interface StoreLike {
  getState(): unknown;
  subscribe(listener: () => void): () => void;
}

interface State {
  businessStore?: { userData?: UserRecord; isOffline?: boolean };
}

/** a source rebuilt only when `userData` is a new object: the slice replaces it on every fetch */
function userDataSource<T>(store: StoreLike, build: (user: UserRecord | undefined) => T): Source<T> {
  let cache: { user: UserRecord | undefined; value: T } | null = null;
  return {
    read() {
      const user = (store.getState() as State)?.businessStore?.userData;
      if (!cache || cache.user !== user) cache = { user, value: build(user) };
      return cache.value;
    },
    subscribe: (listener) => store.subscribe(listener),
  };
}

/** online and kiosk mode, with one object until either flag changes */
function envSource(store: StoreLike): Source<Omit<EnvFacts, "surface">> {
  const platform = detectPlatform();
  let current: Omit<EnvFacts, "surface"> | null = null;
  return {
    read() {
      const state = store.getState() as State;
      const online = !state?.businessStore?.isOffline;
      const kiosk = KIOSK_ENTRY_ENABLED && selectKioskEnabled(state);
      if (!current || current.online !== online || current.kiosk !== kiosk) current = { platform, online, kiosk };
      return current;
    },
    subscribe: (listener) => store.subscribe(listener),
  };
}

export function createStoreSources(store: StoreLike): FactSources {
  return {
    subject: userDataSource(store, subjectFrom),
    tenant: userDataSource(store, configFrom),
    env: envSource(store),
  };
}
